/**
 * LayoutsTabContent.tsx
 * Contenu de l'onglet Layouts
 * Layouts enrichis pour photobooth avec cadres multiples
 */
import React, { useState } from 'react';

import { useAppStore } from '../../../services/StateService';
import { createDefaultShape } from '../../../types/ShapeTypes';
import { useToast } from '../common/ToastProvider';
import { LayoutService } from '../../../services/LayoutService';
import { logger } from '../../../utils/ProfessionalLogger';

/**
 * Composant LayoutsTabContent
 */
export function LayoutsTabContent(): React.ReactElement {
  const addShape = useAppStore(state => state.addShape);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);

  const layouts = [
    { id: 'single', label: 'Plein écran (1 photo)', preview: 'layout-single', icon: '🖼️' },
    {
      id: 'horizontal2',
      label: '2 photos horizontales',
      preview: 'layout-horizontal2',
      icon: '📷📷',
    },
    { id: 'vertical2', label: '2 photos verticales', preview: 'layout-vertical2', icon: '📷\n📷' },
    { id: 'strip3', label: 'Bande 3 photos', preview: 'layout-strip', icon: '📷📷📷' },
    { id: 'column3', label: 'Colonne 3 photos', preview: 'layout-column3', icon: '📷\n📷\n📷' },
    { id: 'grid2x2', label: 'Grille 2×2', preview: 'layout-grid', icon: '📷📷\n📷📷' },
  ] as const;

  const handleLayout = (layoutId: (typeof layouts)[number]['id']) => {
    const margin = 20; // Marge en pixels
    const spacing = 20; // Espacement entre les cadres

    // Calculer les dimensions disponibles (avec marges)
    const availableWidth = templateWidth - margin * 2;
    const availableHeight = templateHeight - margin * 2;

    switch (layoutId) {
      case 'single': {
        // Plein écran avec marges
        const shape = createDefaultShape('photoSpace', margin, margin);
        shape.width = availableWidth;
        shape.height = availableHeight;
        shape.photoIndex = 1;
        addShape(shape);
        break;
      }
      case 'horizontal2': {
        // 2 images horizontales
        const photoWidth = (availableWidth - spacing) / 2;
        const photoHeight = availableHeight;
        for (let i = 0; i < 2; i++) {
          const shape = createDefaultShape(
            'photoSpace',
            margin + i * (photoWidth + spacing),
            margin
          );
          shape.width = photoWidth;
          shape.height = photoHeight;
          shape.photoIndex = i + 1;
          addShape(shape);
        }
        break;
      }
      case 'vertical2': {
        // 2 images verticales
        const photoWidth = availableWidth;
        const photoHeight = (availableHeight - spacing) / 2;
        for (let i = 0; i < 2; i++) {
          const shape = createDefaultShape(
            'photoSpace',
            margin,
            margin + i * (photoHeight + spacing)
          );
          shape.width = photoWidth;
          shape.height = photoHeight;
          shape.photoIndex = i + 1;
          addShape(shape);
        }
        break;
      }
      case 'strip3': {
        // 3 images horizontales
        const photoWidth = (availableWidth - spacing * 2) / 3;
        const photoHeight = availableHeight;
        for (let i = 0; i < 3; i++) {
          const shape = createDefaultShape(
            'photoSpace',
            margin + i * (photoWidth + spacing),
            margin
          );
          shape.width = photoWidth;
          shape.height = photoHeight;
          shape.photoIndex = i + 1;
          addShape(shape);
        }
        break;
      }
      case 'column3': {
        // 3 images verticales
        const photoWidth = availableWidth;
        const photoHeight = (availableHeight - spacing * 2) / 3;
        for (let i = 0; i < 3; i++) {
          const shape = createDefaultShape(
            'photoSpace',
            margin,
            margin + i * (photoHeight + spacing)
          );
          shape.width = photoWidth;
          shape.height = photoHeight;
          shape.photoIndex = i + 1;
          addShape(shape);
        }
        break;
      }
      case 'grid2x2': {
        // 4 images en grille 2×2
        const photoWidth = (availableWidth - spacing) / 2;
        const photoHeight = (availableHeight - spacing) / 2;
        let index = 1;
        for (let row = 0; row < 2; row++) {
          for (let col = 0; col < 2; col++) {
            const shape = createDefaultShape(
              'photoSpace',
              margin + col * (photoWidth + spacing),
              margin + row * (photoHeight + spacing)
            );
            shape.width = photoWidth;
            shape.height = photoHeight;
            shape.photoIndex = index++;
            addShape(shape);
          }
        }
        break;
      }
    }
  };

  const handleSaveLayout = async () => {
    const shapes = useAppStore.getState().shapes;
    const photoSpaces = shapes.filter(s => s.type === 'photoSpace');

    if (photoSpaces.length === 0) {
      addToast({ type: 'warning', message: 'Aucun cadre photo à sauvegarder' });
      return;
    }

    const layoutName = prompt('Nom de la mise en page:');
    if (!layoutName) {
      return;
    }

    try {
      setSaving(true);
      const layoutData = {
        name: layoutName,
        frameCount: photoSpaces.length,
        frames: photoSpaces.map(s => ({
          x: s.x || 0,
          y: s.y || 0,
          width: s.width || 0,
          height: s.height || 0,
          photoIndex: s.photoIndex || 0,
        })),
        format: {
          width: templateWidth,
          height: templateHeight,
        },
      };

      const _layoutId = await LayoutService.getInstance().saveLayout(layoutData);
      addToast({ type: 'success', message: 'Mise en page sauvegardée avec succès' });
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde', { error }, 'LayoutsTabContent');
      addToast({ type: 'error', message: 'Erreur lors de la sauvegarde de la mise en page' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 'var(--spacing-lg)' }}>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <button
          onClick={handleSaveLayout}
          disabled={saving}
          style={{
            width: '100%',
            padding: 'var(--spacing-sm) var(--spacing-md)',
            background: 'var(--color-accent, #3b82f6)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Sauvegarde...' : '💾 Sauvegarder la mise en page'}
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 'var(--spacing-md)',
        }}
      >
        {layouts.map(layout => (
          <button
            key={layout.id}
            className="drawer-item"
            style={{
              textAlign: 'left',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-md)',
              background: 'var(--color-bg-secondary)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => handleLayout(layout.id)}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor =
                'var(--color-accent-border, rgba(59, 130, 246, 0.3))';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div
              style={{
                fontSize: '32px',
                marginBottom: 'var(--spacing-sm)',
                textAlign: 'center',
                whiteSpace: 'pre-line',
              }}
            >
              {layout.icon}
            </div>
            <p className="drawer-item-title" style={{ textAlign: 'center', margin: 0 }}>
              {layout.label}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default LayoutsTabContent;
